// Unified input abstraction across keyboard+mouse, virtual joystick, gyroscope.
// Output is normalized InputState that the flight model consumes.
//
// Detection at boot: keyboard listeners always installed; touch input enables
// nipplejs joysticks; gyroscope is opt-in via user gesture (iOS permission).

import nipplejs, { type JoystickManager } from 'nipplejs';

export interface InputState {
  pitch: number;     // -1 nose-down ... +1 nose-up
  yaw: number;       // -1 turn-left ... +1 turn-right
  roll: number;      // -1 bank-left ... +1 bank-right
  throttle: number;  // 0..1
  boost: boolean;    // sprint / afterburner
  fire: boolean;     // pressed-and-held trigger
  firePulse: boolean; // single-shot pulse, consumed once per tick
  brake: boolean;
}

export type InputMode = 'keyboard' | 'joystick' | 'gyro';

export class Input {
  state: InputState = {
    pitch: 0, yaw: 0, roll: 0, throttle: 0.7, boost: false, fire: false, firePulse: false, brake: false,
  };
  mode: InputMode;
  hasTouch: boolean;
  hasGyro = false;
  gyroEnabled = false;

  private keys = new Set<string>();
  private leftJoystick: JoystickManager | null = null;
  private rightJoystick: JoystickManager | null = null;
  private joyLeft = { x: 0, y: 0 };
  private joyRight = { x: 0, y: 0 };
  private gyroBeta = 0;     // pitch (front-back tilt)
  private gyroGamma = 0;    // roll (left-right tilt)
  private gyroBaseBeta = 45; // calibrated when user enables
  private mouseDX = 0;
  private mouseDY = 0;
  private pointerLocked = false;
  private firePulseFlag = false;

  // Bindings root for joysticks (created lazily when joystick mode activates).
  private joyZoneLeft: HTMLDivElement | null = null;
  private joyZoneRight: HTMLDivElement | null = null;
  private fireButton: HTMLDivElement | null = null;

  constructor() {
    this.hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.hasGyro = typeof DeviceOrientationEvent !== 'undefined';
    this.mode = this.hasTouch ? 'joystick' : 'keyboard';
    this.bindKeyboard();
    this.bindMouse();
    if (this.hasTouch) this.mountJoysticks();
  }

  setMode(mode: InputMode): void {
    this.mode = mode;
    if (mode === 'joystick') {
      this.mountJoysticks();
    } else if (mode === 'gyro') {
      // joysticks stay mounted for fire/boost buttons; pitch/roll come from gyro
      this.mountJoysticks();
    }
  }

  /**
   * Must be called from a user gesture (e.g. button click).
   * Returns true if gyro was successfully enabled.
   */
  async enableGyro(): Promise<boolean> {
    if (!this.hasGyro) return false;
    type IOSDeviceOrientationEvent = typeof DeviceOrientationEvent & { requestPermission?: () => Promise<'granted' | 'denied'> };
    const Ctor = DeviceOrientationEvent as IOSDeviceOrientationEvent;
    if (typeof Ctor.requestPermission === 'function') {
      try {
        const r = await Ctor.requestPermission();
        if (r !== 'granted') return false;
      } catch {
        return false;
      }
    }
    window.addEventListener('deviceorientation', (e) => {
      this.gyroBeta = e.beta ?? 0;
      this.gyroGamma = e.gamma ?? 0;
    });
    // Calibrate to current pose so the player isn't fighting a 45° offset.
    setTimeout(() => { this.gyroBaseBeta = this.gyroBeta; }, 250);
    this.gyroEnabled = true;
    this.setMode('gyro');
    return true;
  }

  consumeFirePulse(): boolean {
    const f = this.firePulseFlag;
    this.firePulseFlag = false;
    return f;
  }

  /** Called every frame before flight update. */
  tick(_dt: number): void {
    const k = this.keys;

    // ---------- Keyboard branch ----------
    if (this.mode === 'keyboard') {
      const fwd = (k.has('KeyW') || k.has('ArrowUp')) ? 1 : 0;
      const back = (k.has('KeyS') || k.has('ArrowDown')) ? 1 : 0;
      const left = (k.has('KeyA') || k.has('ArrowLeft')) ? 1 : 0;
      const right = (k.has('KeyD') || k.has('ArrowRight')) ? 1 : 0;
      this.state.pitch = fwd - back;
      this.state.yaw = right - left;
      // Mouse adds yaw + pitch with smaller weight; clears each frame.
      this.state.yaw = clamp(this.state.yaw + this.mouseDX * 1.6, -1, 1);
      this.state.pitch = clamp(this.state.pitch - this.mouseDY * 1.6, -1, 1);
      this.mouseDX = 0; this.mouseDY = 0;
      this.state.roll = (k.has('KeyQ') ? 1 : 0) - (k.has('KeyE') ? 1 : 0);
      this.state.throttle = k.has('ShiftLeft') ? 1 : 0.7;
      this.state.boost = k.has('ShiftLeft');
      this.state.brake = k.has('Space');
      this.state.fire = k.has('Mouse0') || k.has('KeyJ');
    }

    // ---------- Joystick branch (touch) ----------
    if (this.mode === 'joystick') {
      this.state.yaw = this.joyLeft.x;
      this.state.pitch = -this.joyLeft.y;        // nipplejs y is inverted
      this.state.roll = 0;
      this.state.throttle = clamp(0.5 + this.joyRight.y * 0.5, 0, 1);
      this.state.boost = this.joyRight.y > 0.85;
      this.state.brake = this.joyRight.y < -0.7;
    }

    // ---------- Gyro branch (touch + tilt) ----------
    if (this.mode === 'gyro') {
      // beta: -180..180, normalize around calibrated base. Lock to 35° max.
      const dBeta = clamp((this.gyroBeta - this.gyroBaseBeta) / 35, -1, 1);
      const gamma = clamp(this.gyroGamma / 35, -1, 1);
      this.state.pitch = -dBeta;  // tilt forward = pitch down
      this.state.yaw = gamma;     // tilt right = yaw right (also drives roll)
      this.state.roll = gamma * 0.6;
      this.state.throttle = clamp(0.5 + this.joyRight.y * 0.5, 0, 1);
      this.state.boost = this.joyRight.y > 0.85;
      this.state.brake = this.joyRight.y < -0.7;
    }

    if (this.firePulseFlag === false && this.state.fire) {
      // Fire is held -> we don't auto-pulse here; pulse is only set on edge.
    }
  }

  // ============== Internal bindings ==============

  private bindKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      if (e.code === 'KeyJ' || e.code === 'Space' && false) {
        this.firePulseFlag = true;
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
  }

  private bindMouse(): void {
    const canvas = () => document.querySelector('canvas');
    window.addEventListener('mousedown', (e) => {
      if (e.button === 0) { this.keys.add('Mouse0'); this.firePulseFlag = true; }
    });
    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.keys.delete('Mouse0');
    });
    window.addEventListener('mousemove', (e) => {
      if (!this.pointerLocked) return;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      this.mouseDX += (e.movementX ?? 0) / w;
      this.mouseDY += (e.movementY ?? 0) / h;
    });
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement != null;
    });
    // Click on canvas requests pointer lock for FPS-style mouse aiming.
    document.addEventListener('click', () => {
      if (this.mode === 'keyboard' && !this.pointerLocked) {
        canvas()?.requestPointerLock?.().catch(() => {});
      }
    });
  }

  private mountJoysticks(): void {
    if (this.leftJoystick) return;

    const left = document.createElement('div');
    left.id = 'joy-left';
    Object.assign(left.style, {
      position: 'fixed', left: '0', bottom: '0', width: '40vw', height: '40vw',
      maxWidth: '240px', maxHeight: '240px', zIndex: '50', touchAction: 'none',
    } as CSSStyleDeclaration);
    document.body.appendChild(left);
    this.joyZoneLeft = left;

    const right = document.createElement('div');
    right.id = 'joy-right';
    Object.assign(right.style, {
      position: 'fixed', right: '0', bottom: '0', width: '40vw', height: '40vw',
      maxWidth: '240px', maxHeight: '240px', zIndex: '50', touchAction: 'none',
    } as CSSStyleDeclaration);
    document.body.appendChild(right);
    this.joyZoneRight = right;

    this.leftJoystick = nipplejs.create({
      zone: left, mode: 'static', position: { left: '50%', top: '50%' },
      color: '#88ddff', size: 110, restOpacity: 0.4,
    });
    this.rightJoystick = nipplejs.create({
      zone: right, mode: 'static', position: { left: '50%', top: '50%' },
      color: '#ffaa66', size: 110, restOpacity: 0.4,
    });

    this.leftJoystick.on('move', (_e, d) => {
      const dist = Math.min(d.distance / 50, 1);
      const ang = d.angle.radian;
      this.joyLeft.x = Math.cos(ang) * dist;
      this.joyLeft.y = Math.sin(ang) * dist;
    });
    this.leftJoystick.on('end', () => { this.joyLeft.x = 0; this.joyLeft.y = 0; });

    this.rightJoystick.on('move', (_e, d) => {
      const dist = Math.min(d.distance / 50, 1);
      const ang = d.angle.radian;
      this.joyRight.x = Math.cos(ang) * dist;
      this.joyRight.y = Math.sin(ang) * dist;
    });
    this.rightJoystick.on('end', () => { this.joyRight.x = 0; this.joyRight.y = 0; });

    // Fire button (always bottom-right above the throttle stick).
    const fire = document.createElement('div');
    fire.id = 'btn-fire';
    Object.assign(fire.style, {
      position: 'fixed', right: '12px', bottom: 'calc(45vw + 12px)',
      width: '64px', height: '64px', borderRadius: '50%',
      background: 'rgba(255,80,80,0.4)', border: '2px solid rgba(255,200,200,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: '14px', userSelect: 'none', zIndex: '52',
      touchAction: 'none',
    } as CSSStyleDeclaration);
    fire.textContent = 'FIRE';
    document.body.appendChild(fire);
    this.fireButton = fire;
    const onDown = (ev: Event) => { ev.preventDefault(); this.state.fire = true; this.firePulseFlag = true; };
    const onUp = (ev: Event) => { ev.preventDefault(); this.state.fire = false; };
    fire.addEventListener('touchstart', onDown, { passive: false });
    fire.addEventListener('touchend', onUp, { passive: false });
    fire.addEventListener('mousedown', onDown);
    fire.addEventListener('mouseup', onUp);
  }

  destroyJoysticks(): void {
    this.leftJoystick?.destroy();
    this.rightJoystick?.destroy();
    this.joyZoneLeft?.remove();
    this.joyZoneRight?.remove();
    this.fireButton?.remove();
    this.leftJoystick = this.rightJoystick = null;
    this.joyZoneLeft = this.joyZoneRight = this.fireButton = null;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
